// Step 1 · Alapok: a name and a mode. Pressing "Tovább" creates the campaign
// as a draft (POST /campaign-groups/); everything after is the other steps.
// Neither can be changed later — the API has no rename and no mode switch —
// so an existing campaign shows them and says so.

import { useState } from "react";
import type { CampaignGroupMode, CampaignGroupSummary, SendingIdentity } from "@/lib/api";
import { createCampaignGroup } from "@/lib/api";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { NativeSelect } from "@/components/ui/native-select";
import { DetailList } from "@/components/emc/DetailList";
import { Field } from "@/components/emc/Field";
import { ErrorState } from "@/components/emc/states";
import { labelOf } from "@/components/emc/status";
import { cn } from "@/lib/utils";
import { messageOf } from "@/pages/campaigns/model";
import { StepActions } from "@/pages/campaigns/parts";

const NAME_MAX = 200;

const MODES: { value: CampaignGroupMode; title: string; text: string }[] = [
  {
    value: "single_language",
    title: "Egynyelvű",
    text: "Egy levél megy minden országba; minden ország a sablon a saját nyelvére írt változatát kapja, ha van ilyen.",
  },
  {
    value: "localized",
    title: "Lokalizált",
    text: "Országonként saját szöveg: gépi fordítás a forrásország szövegéből, és minden országot egy embernek jóvá kell hagynia, mielőtt kimehet.",
  },
];

export function BasicsStep({
  group,
  identities,
  onCreated,
  onNext,
}: {
  /** The campaign, once it exists; null while it is being named. */
  group: CampaignGroupSummary | null;
  identities: SendingIdentity[];
  onCreated: (id: number) => void;
  onNext: () => void;
}) {
  const [name, setName] = useState("");
  const [mode, setMode] = useState<CampaignGroupMode>("single_language");
  const [source, setSource] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sourceCountry = source || identities[0]?.country || "";

  if (group !== null) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Alapok</CardTitle>
          <CardDescription>
            A név és a mód a létrehozáskor dől el, utólag nem módosítható.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-5">
          <DetailList
            items={[
              { label: "Név", value: group.name },
              {
                label: "Mód",
                value:
                  group.mode === "localized"
                    ? `${labelOf("campaignMode", group.mode)} — forrásország: ${group.source_country ?? "—"}`
                    : labelOf("campaignMode", group.mode),
              },
              { label: "Létrehozta", value: group.created_by, muted: true },
            ]}
          />
          <StepActions next={{ label: "Tovább a címzettekhez", onClick: onNext }} />
        </CardContent>
      </Card>
    );
  }

  const trimmed = name.trim();
  const reason =
    trimmed === ""
      ? "A név nem lehet üres. Adj nevet a kampánynak, mielőtt továbblépsz."
      : mode === "localized" && sourceCountry === ""
        ? "Lokalizált módban meg kell adni a forrásországot."
        : null;

  async function create() {
    if (reason !== null) return;
    setBusy(true);
    setError(null);
    try {
      const created = await createCampaignGroup({
        name: trimmed,
        mode,
        source_country: mode === "localized" ? sourceCountry : null,
      });
      onCreated(created.id);
    } catch (raised: unknown) {
      setError(messageOf(raised));
      setBusy(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Alapok</CardTitle>
        <CardDescription>
          A kampány neve csak nálunk látszik, a címzettek nem látják. A lábléc, a cégadatok
          és a leiratkozó szöveg mindkét módban országonként a feladó fiókjából jön — kampányból
          nem írható felül.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-5">
        {error !== null && <ErrorState message={error} />}
        <Field label="A kampány neve" htmlFor="campaign-name" className="max-w-prose">
          <Input
            id="campaign-name"
            value={name}
            maxLength={NAME_MAX}
            placeholder="pl. Black Friday — nyitó levél"
            onChange={(event) => setName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") void create();
            }}
            autoFocus
          />
        </Field>

        <fieldset className="m-0 flex flex-col gap-2 border-0 p-0">
          <legend className="mb-2 text-body font-medium">Mód</legend>
          <div className="grid gap-3 md:grid-cols-2">
            {MODES.map((option) => (
              <label
                key={option.value}
                className={cn(
                  "flex cursor-pointer items-start gap-3 rounded-card border border-line bg-surface p-4 hover:bg-surface-2",
                  mode === option.value && "border-primary bg-primary-soft hover:bg-primary-soft",
                )}
              >
                <input
                  type="radio"
                  name="campaign-mode"
                  value={option.value}
                  checked={mode === option.value}
                  onChange={() => setMode(option.value)}
                  className="mt-1 size-4 shrink-0 accent-primary"
                />
                <span className="flex flex-col gap-1">
                  <span className="font-medium">{option.title}</span>
                  <span className="text-muted-foreground">{option.text}</span>
                </span>
              </label>
            ))}
          </div>
        </fieldset>

        {mode === "localized" && (
          <Field
            label="Forrásország"
            htmlFor="campaign-source"
            hint="Ennek az országnak a szövegéből készül a fordítás a többi országnak."
            className="max-w-80"
          >
            <NativeSelect
              id="campaign-source"
              className="w-full"
              value={sourceCountry}
              onChange={(event) => setSource(event.target.value)}
            >
              {identities.map((row) => (
                <option key={row.country} value={row.country}>
                  {row.country} — {row.label}
                </option>
              ))}
            </NativeSelect>
          </Field>
        )}

        <StepActions
          next={{
            label: "Tovább a címzettekhez",
            onClick: () => void create(),
            disabled: reason !== null,
            reason,
            busy,
          }}
        />
        <p className="-mt-2 text-right text-label text-muted-foreground">
          A kampány vázlatként jön létre; semmi nem megy ki, amíg az utolsó lépésben el nem indítod.
        </p>
      </CardContent>
    </Card>
  );
}
